import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  Stack,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Flag as FlagIcon,
  FlagOutlined as UnflagIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  DeleteSweep as PurgeIcon,
} from '@mui/icons-material';
import { searchRecipes, flagRecipe, unflagRecipe, deleteRecipe, purgeFlaggedRecipes, Recipe, PurgeResult } from '../adminApi';

const RecipeList: React.FC = () => {
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);
  const [showStubsOnly, setShowStubsOnly] = useState(false);
  const [showNoImage, setShowNoImage] = useState(false);
  const [showDalleOnly, setShowDalleOnly] = useState(false);
  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState<PurgeResult | null>(null);

  const fetchRecipes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await searchRecipes({
        query: searchQuery || undefined,
        page: page + 1,
        limit: rowsPerPage,
        flagged: showFlaggedOnly ? true : null,
        hasIngredients: showStubsOnly ? false : null,
        noImage: showNoImage ? true : null,
        dalleOnly: showDalleOnly ? true : null,
      });
      setRecipes(result.recipes || []);
      setTotal(result.total);
    } catch (err: any) {
      setError(err.message || 'Failed to load recipes');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, page, rowsPerPage, showFlaggedOnly, showStubsOnly, showNoImage, showDalleOnly]);

  useEffect(() => {
    fetchRecipes();
  }, [fetchRecipes]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleFlag = async (recipe: Recipe) => {
    try {
      if (recipe.flagged_for_review) {
        await unflagRecipe(recipe.recipe_id);
      } else {
        const reason = prompt('Enter reason for flagging:');
        if (!reason) return;
        await flagRecipe(recipe.recipe_id, reason);
      }
      fetchRecipes();
    } catch (err: any) {
      alert(err.message || 'Failed to update flag');
    }
  };

  const handleDelete = async (recipe: Recipe) => {
    if (!window.confirm(`Are you sure you want to delete "${recipe.name}"?`)) return;

    const reason = prompt('Enter reason for deletion (optional):');
    try {
      await deleteRecipe(recipe.recipe_id, reason || undefined);
      fetchRecipes();
    } catch (err: any) {
      alert(err.message || 'Failed to delete recipe');
    }
  };

  const handlePurgeFlagged = async () => {
    if (!window.confirm(
      'This will permanently delete ALL flagged recipes that are not in any active meal plan. This cannot be undone.\n\nContinue?'
    )) return;

    setPurging(true);
    setPurgeResult(null);
    setError(null);
    try {
      const result = await purgeFlaggedRecipes();
      setPurgeResult(result);
      fetchRecipes();
    } catch (err: any) {
      setError(err.message || 'Failed to purge flagged recipes');
    } finally {
      setPurging(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" fontWeight="bold">
          Recipes
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="error"
            startIcon={purging ? <CircularProgress size={18} color="inherit" /> : <PurgeIcon />}
            onClick={handlePurgeFlagged}
            disabled={purging}
          >
            {purging ? 'Purging...' : 'Purge Flagged'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/recipes/new')}
          >
            Create Recipe
          </Button>
        </Stack>
      </Box>

      {/* Search and filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <TextField
            placeholder="Search recipes..."
            value={searchQuery}
            onChange={handleSearch}
            size="small"
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showFlaggedOnly}
                onChange={(e) => {
                  setShowFlaggedOnly(e.target.checked);
                  setPage(0);
                }}
              />
            }
            label="Flagged only"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showStubsOnly}
                onChange={(e) => {
                  setShowStubsOnly(e.target.checked);
                  setPage(0);
                }}
              />
            }
            label="Stubs only (no ingredients)"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showNoImage}
                onChange={(e) => {
                  setShowNoImage(e.target.checked);
                  setPage(0);
                }}
              />
            }
            label="No image"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showDalleOnly}
                onChange={(e) => {
                  setShowDalleOnly(e.target.checked);
                  setPage(0);
                }}
              />
            }
            label="Legacy DALL-E images"
          />
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {purgeResult && (
        <Alert
          severity={purgeResult.deleted > 0 ? 'success' : 'info'}
          sx={{ mb: 2 }}
          onClose={() => setPurgeResult(null)}
        >
          Purged {purgeResult.deleted} flagged recipe{purgeResult.deleted !== 1 ? 's' : ''}.
          {purgeResult.skipped > 0 && (
            <> Skipped {purgeResult.skipped} (still in active plans: {purgeResult.skipped_names.join(', ')}).</>
          )}
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell align="center">Ingredients</TableCell>
              <TableCell align="center">Steps</TableCell>
              <TableCell align="center">Prep Time</TableCell>
              <TableCell align="center">Usage</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : recipes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  No recipes found
                </TableCell>
              </TableRow>
            ) : (
              recipes.map((recipe) => (
                <TableRow
                  key={recipe.recipe_id}
                  hover
                  sx={{
                    backgroundColor: recipe.flagged_for_review ? '#fff3e0' : 'inherit',
                  }}
                >
                  <TableCell>{recipe.recipe_id}</TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {recipe.name}
                      </Typography>
                      {recipe.description && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {recipe.description}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={recipe.ingredient_count || 0}
                      size="small"
                      color={recipe.ingredient_count ? 'default' : 'warning'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={recipe.step_count || 0}
                      size="small"
                      color={recipe.step_count ? 'default' : 'warning'}
                    />
                  </TableCell>
                  <TableCell align="center">{recipe.prep_time || '-'} min</TableCell>
                  <TableCell align="center">{recipe.usage_count || 0}</TableCell>
                  <TableCell align="center">
                    {recipe.flagged_for_review ? (
                      <Tooltip title={recipe.flag_reason || 'Flagged'}>
                        <Chip label="Flagged" color="error" size="small" icon={<FlagIcon />} />
                      </Tooltip>
                    ) : recipe.ingredient_count === 0 ? (
                      <Chip label="Stub" color="warning" size="small" />
                    ) : (
                      <Chip label="OK" color="success" size="small" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View/Edit">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/admin/recipes/${recipe.recipe_id}`)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={recipe.flagged_for_review ? 'Unflag' : 'Flag'}>
                      <IconButton size="small" onClick={() => handleFlag(recipe)}>
                        {recipe.flagged_for_review ? (
                          <UnflagIcon color="warning" />
                        ) : (
                          <FlagIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(recipe)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </TableContainer>
    </Box>
  );
};

export default RecipeList;
